import toast from 'react-hot-toast'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useState } from 'react'
import * as yup from 'yup'
import { Damaged, UnitLoc } from 'src/api/models'
import { useForm } from 'src/forms'
import { setApiErrors } from 'src/forms/errors'
import { Notification } from 'src/components/Notification'
import { skuit } from 'src/services/skuit'
import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { DamagedLabel } from 'src/components/PrintLabel/DamagedLabel'
import { BulkPrint } from 'src/components/Print/Bulk'

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface DamageProps {
  count: number
  damage: string
  by_us: boolean
  notes: string
}

const damageSchema = yup.object().shape({
  count: yup.number().required().default(1).label('Number to Report Damaged'),
  damage: yup.string().required().oneOf(Object.values(Damaged.Damage)).label('Damage'),
  by_us: yup.bool().required().label('By Us'),
  notes: yup.string().optional().label('Notes'),
})

interface Props {
  open: boolean
  handleClose: () => void
  unit?: UnitLoc
  onSave?: () => void
}

export const ReportDamageDialog = (props: Props) => {
  const { open, handleClose, unit, onSave } = props
  const [damages, setDamages] = useState<Damaged[]>([])
  const classes = useStyles()
  const { Form, Field, data: formData } = useForm(damageSchema)
  const { print, renderPrintComponent } = useAsyncPrint()

  const onClose = () => {
    setDamages([])
    handleClose()
  }

  const onSubmit = (data: DamageProps) => {
    if (!unit) {
      toast.error(() => <Notification message="Need to specify what unit to mark as damaged" />)
      return
    }
    skuit.sc.units
      .damaged(unit.location?.guid || '', unit.product?.guid || '', {...data, batch: unit?.batch?.guid})
      .then((damages) => {
        setDamages(damages)
        toast.success(() => (
          <Notification message={`Marked ${formData.values.count} as damaged!`} />
        ))
      })
      .catch((error) => {
        if (error?.response?.data?.details) {
          toast.error(() => <Notification message={`Failed to mark as damaged.`} />)
        } else {
          toast.error(() => <Notification message={`Failed to mark as as damaged: ${error}`} />)
        }
        setApiErrors(formData.setError, error)
      })
    onSave?.()
  }

  const handlePrint = () => {
    print((props) => (
      <BulkPrint
        {...props}
        items={damages}
        render={({ obj, onReadyToPrint }) => (
          <DamagedLabel obj={obj} onReadyToPrint={onReadyToPrint} />
        )}
      />
    ))
  }

  if (damages.length) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        {renderPrintComponent()}
        <DialogTitle>Report Damages</DialogTitle>
        <DialogContent>
          Created {damages.length} damage ticket(s).
          <br />
          <Button variant="outlined" onClick={handlePrint}>
            Print {damages.length} damage labels.
          </Button>
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" autoFocus onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>Report Damages</DialogTitle>
        <DialogContent>
          <Field
            name="count"
            defaultValue={1}
            renderField={({ name, value, setValue, error }) => (
              <TextField
                id={name}
                name={name}
                type="number"
                variant="standard"
                label="Number to Report Damaged"
                error={!!error}
                helperText={error}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue(name, e.target.value || undefined)
                }
                value={value}
              />
            )}
          />
          <Field
            name="damage"
            getSubmitValue={(value) => value}
            renderField={({ name, value, setValue, error }) => (
              <FormControl fullWidth>
                <InputLabel id={name}>Damage</InputLabel>
                <Select
                  id={name}
                  name={name}
                  label="Damage"
                  error={!!error}
                  onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                    setValue(name, event.target.value as string)
                  }}
                  value={value || ''}
                >
                  {Object.entries(Damaged.Damage).map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          <Field
            name="by_us"
            defaultValue={false}
            renderField={({ name, value, setValue }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    id={name}
                    name={name}
                    checked={Boolean(value)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setValue(name, e.target.checked)
                    }
                  />
                }
                label="By Us?"
              />
            )}
          />
          <Field
            name="notes"
            renderField={({ name, value, setValue, error }) => (
              <TextField
                id={name}
                name={name}
                variant="standard"
                multiline
                rows={4}
                label="Notes"
                error={!!error}
                helperText={error}
                fullWidth
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue(name, e.target.value)
                }
                value={value || ''}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
